import { useEffect } from 'react'
import { useRouter } from 'next/router'
import getConfig from 'next/config'
import {
  getAlternateUrls,
  getCanonicalUrl,
  getLocaleFromPath,
  getPublicPath,
  LOCALE_OG,
  SEO_HEAD_IDS,
} from '@/lib/locales'

function upsertLink(id, attributes) {
  let el = document.getElementById(id)
  if (!el) {
    el = document.createElement('link')
    el.id = id
    document.head.appendChild(el)
  }

  Object.entries(attributes).forEach(([name, value]) => {
    if (value == null) return
    el.setAttribute(name, value)
  })
}

function removeAllHreflangLinks() {
  // Remove every managed hreflang node, including stale ones from disabled
  // locales (ar) or bad ids like seo-hreflang-undefined.
  document.querySelectorAll('link[id^="seo-hreflang-"]').forEach((el) => el.remove())
}

function removeSeoLinks() {
  document.getElementById(SEO_HEAD_IDS.canonical)?.remove()
  removeAllHreflangLinks()
}

export default function HreflangLinks({ path: pathFromProps, ogImage, skipSeoLinks }) {
  const router = useRouter()
  const {
    publicRuntimeConfig: { host },
  } = getConfig()

  const path = pathFromProps || getPublicPath(router.asPath)

  useEffect(() => {
    if (skipSeoLinks) {
      removeSeoLinks()
      return
    }

    const locale = getLocaleFromPath(path)
    const canonicalUrl = getCanonicalUrl(path, host)
    const alternates = getAlternateUrls(path, host)

    upsertLink(SEO_HEAD_IDS.canonical, {
      rel: 'canonical',
      href: canonicalUrl,
    })

    removeAllHreflangLinks()
    alternates.forEach(({ hrefLang, href }) => {
      if (!hrefLang || !href) return

      upsertLink(SEO_HEAD_IDS.hreflang(hrefLang), {
        rel: 'alternate',
        href,
        hreflang: hrefLang,
      })
    })

    const ogUrl = document.getElementById(SEO_HEAD_IDS.ogUrl)
    if (ogUrl) ogUrl.setAttribute('content', canonicalUrl)

    const ogLocale = document.getElementById(SEO_HEAD_IDS.ogLocale)
    if (ogLocale) ogLocale.setAttribute('content', LOCALE_OG[locale])

    const ogImageEl = document.getElementById(SEO_HEAD_IDS.ogImage)
    if (ogImageEl && ogImage) ogImageEl.setAttribute('content', ogImage)
  }, [host, ogImage, path, skipSeoLinks])

  return null
}
